import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, Eye, CheckCircle, XCircle, Clock, AlertTriangle,
  Loader2, Phone, User, IndianRupee, RefreshCw, Calendar
} from 'lucide-react';

const MobileServiceApproval = () => {
  const { user: authUser } = useAuth();
  // Fallback to localStorage if AuthContext user is not available
  const localUser = React.useMemo(() => {
    try {
      const storedUser = localStorage.getItem('suntech_mobile_user') || localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  }, []);
  const user = authUser || localUser;
  
  const [pendingServices, setPendingServices] = useState([]);
  const [rejectedServices, setRejectedServices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Role checks
  const isAdmin = user?.role === 'Admin';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const canAccess = isAdmin || isBackOfficeAccounts;

  const fetchPendingServices = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const response = await api.get('/service-approvals/pending', { params });
      setPendingServices(response.data);
    } catch (error) {
      console.error('Error fetching pending services:', error);
    }
  }, [searchTerm]);

  const fetchRejectedServices = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const response = await api.get('/service-approvals/rejected', { params });
      setRejectedServices(response.data);
    } catch (error) {
      console.error('Error fetching rejected services:', error);
    }
  }, [searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/service-approvals/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    if (canAccess) {
      setLoading(true);
      Promise.all([fetchStats(), fetchPendingServices(), fetchRejectedServices()])
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [canAccess, fetchStats, fetchPendingServices, fetchRejectedServices]);

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([fetchStats(), fetchPendingServices(), fetchRejectedServices()])
      .finally(() => setLoading(false));
  };

  const handleViewService = async (service) => {
    try {
      const response = await api.get(`/service-approvals/${service.id}`);
      setSelectedService(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load service details');
    }
  };

  const openApprovalDialog = (service, action) => {
    setSelectedService(service);
    setApprovalAction(action);
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    if (!approvalNotes || approvalNotes.trim().length < 3) {
      toast.error('Notes are mandatory (minimum 3 characters)');
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = approvalAction === 'approve' 
        ? `/service-approvals/${selectedService.id}/approve`
        : `/service-approvals/${selectedService.id}/reject`;
      
      await api.post(endpoint, { notes: approvalNotes.trim() });
      
      toast.success(approvalAction === 'approve' 
        ? 'Service approved!' 
        : 'Service rejected!'
      );
      setApprovalDialogOpen(false);
      handleRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${approvalAction} service`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdminReview = async (service) => {
    if (!isAdmin) {
      toast.error('Only Admin can review rejected services');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/service-approvals/${service.id}/admin-review`, { status: 'Approved' });
      toast.success('Marked as reviewed');
      handleRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review');
    } finally {
      setSubmitting(false);
    }
  };

  const getServiceTypeBadge = (service) => {
    if (service.service_type_category === 'Free') {
      return <Badge className="bg-green-100 text-green-800 text-xs">Free</Badge>;
    }
    if (service.payment_status === 'Paid') {
      return <Badge className="bg-blue-100 text-blue-800 text-xs">Paid</Badge>;
    }
    return <Badge className="bg-orange-100 text-orange-800 text-xs">Pending</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (!canAccess) {
    return (
      <div className="p-4 pb-24">
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <XCircle className="w-12 h-12 mx-auto mb-2 text-red-400" />
            <p>Only Admin and Backoffice Accounts can access Service Approvals</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="mobile-service-approvals">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Service Approvals</h1>
          <p className="text-sm text-gray-500">Review and approve services</p>
        </div>
        <Button onClick={handleRefresh} size="sm" variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-800">{stats.pending_count || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">Rejected</p>
                <p className="text-2xl font-bold text-red-800">{stats.rejected_pending_review || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
        <Input
          placeholder="Search by service #, customer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pending" className="text-xs">
            Pending {stats.pending_count > 0 && `(${stats.pending_count})`}
          </TabsTrigger>
          <TabsTrigger value="rejected" className="text-xs">
            Rejected {stats.rejected_pending_review > 0 && `(${stats.rejected_pending_review})`}
          </TabsTrigger>
        </TabsList>

        {/* Pending Tab */}
        <TabsContent value="pending" className="space-y-3">
          {pendingServices.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-300" />
                <p>No pending approvals</p>
              </CardContent>
            </Card>
          ) : (
            pendingServices.map(service => (
              <Card key={service.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-sm">{service.service_no}</p>
                      <p className="text-xs text-gray-500">{formatDate(service.closed_at)}</p>
                    </div>
                    {getServiceTypeBadge(service)}
                  </div>
                  <div className="space-y-1 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <User size={12} />
                      <span className="truncate">{service.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Phone size={12} />
                      <span>{service.customer_mobile}</span>
                    </div>
                    {service.service_charge > 0 && (
                      <div className="flex items-center gap-1 font-medium text-green-700">
                        <IndianRupee size={12} />
                        <span>₹{service.service_charge?.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1 text-xs h-8"
                      onClick={() => handleViewService(service)}
                    >
                      <Eye size={12} className="mr-1" /> View
                    </Button>
                    <Button 
                      size="sm" 
                      className="flex-1 bg-green-600 hover:bg-green-700 text-xs h-8"
                      onClick={() => openApprovalDialog(service, 'approve')}
                    >
                      <CheckCircle size={12} className="mr-1" /> Approve
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      className="flex-1 text-xs h-8"
                      onClick={() => openApprovalDialog(service, 'reject')}
                    >
                      <XCircle size={12} className="mr-1" /> Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Rejected Tab */}
        <TabsContent value="rejected" className="space-y-3">
          {rejectedServices.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <p>No rejected services</p>
              </CardContent>
            </Card>
          ) : (
            rejectedServices.map(service => (
              <Card key={service.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-sm">{service.service_no}</p>
                      <p className="text-xs text-gray-500">Rejected: {formatDate(service.rejected_at)}</p>
                    </div>
                    {service.admin_review_status === 'Approved' ? (
                      <Badge className="bg-green-100 text-green-800 text-xs">Reviewed</Badge>
                    ) : (
                      <Badge className="bg-orange-100 text-orange-800 text-xs">Pending Review</Badge>
                    )}
                  </div>
                  <div className="space-y-1 text-xs text-gray-600 mb-2">
                    <p><span className="font-medium">Customer:</span> {service.customer_name}</p>
                    <p><span className="font-medium">Rejected by:</span> {service.rejected_by_name || '-'}</p>
                    <p className="text-red-600"><span className="font-medium">Reason:</span> {service.rejection_notes || '-'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1 text-xs h-8"
                      onClick={() => handleViewService(service)}
                    >
                      <Eye size={12} className="mr-1" /> View
                    </Button>
                    {isAdmin && service.admin_review_status !== 'Approved' && (
                      <Button 
                        size="sm" 
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-xs h-8"
                        onClick={() => handleAdminReview(service)}
                        disabled={submitting}
                      >
                        Mark Reviewed
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Service Details</DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4 text-sm">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold">{selectedService.service_no}</p>
                <p className="text-gray-600">{selectedService.service_type}</p>
              </div>
              
              <div>
                <Label className="text-xs text-gray-500">Customer</Label>
                <p className="font-medium">{selectedService.customer?.company_name}</p>
                <p className="text-gray-600">{selectedService.customer?.mobile}</p>
              </div>
              
              <div>
                <Label className="text-xs text-gray-500">Service Category</Label>
                <div className="mt-1">{getServiceTypeBadge(selectedService)}</div>
              </div>
              
              {selectedService.service_type_category === 'Free' ? (
                <div className="bg-green-50 p-3 rounded-lg">
                  <Label className="text-xs text-green-600">Free Service Notes</Label>
                  <p>{selectedService.free_service_notes || '-'}</p>
                </div>
              ) : (
                <div className="bg-blue-50 p-3 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-bold">₹{selectedService.service_charge?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <Badge>{selectedService.payment_status}</Badge>
                  </div>
                  {selectedService.payment_status === 'Paid' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mode:</span>
                      <span>{selectedService.payment_mode}</span>
                    </div>
                  )}
                  {selectedService.payment_status === 'Pending' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Due Date:</span>
                      <span className="text-orange-600">{selectedService.payment_due_date}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={approvalAction === 'approve' ? 'text-green-700' : 'text-red-700'}>
              {approvalAction === 'approve' ? '✓ Approve Service' : '✗ Reject Service'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedService && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>{selectedService.service_no}</strong></p>
                <p>{selectedService.customer_name}</p>
              </div>
            )}
            <div>
              <Label>{approvalAction === 'approve' ? 'Approval Notes *' : 'Rejection Notes *'}</Label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder={approvalAction === 'approve' ? 'Enter approval notes...' : 'Enter rejection reason...'}
                rows={3}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 3 characters required</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleApprovalSubmit}
              disabled={submitting || !approvalNotes || approvalNotes.trim().length < 3}
              className={approvalAction === 'approve' ? 'bg-green-600' : 'bg-red-600'}
            >
              {submitting ? 'Processing...' : (approvalAction === 'approve' ? 'Approve' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileServiceApproval;
